import { ComponentProps } from 'react';

import usePlayerMicStatus from '~/modules/hooks/players/use-player-mic-status';
import { PlayerNumber } from '~/modules/players/player-number';

import Ping from './ping';

interface Props extends ComponentProps<'div'> {
  playerNumber: PlayerNumber;
}

/**
 * A slot's own microphone, and what is wrong with it.
 *
 * A warning covers the slot it belongs to rather than sitting beside it. It used to be a tooltip
 * anchored to the slot's right edge and spilling outwards, which put it on top of the NEXT player's
 * slot - so a disconnected Player 1 read as a disconnected Player 2. Two of them overlapped each
 * other, and the fourth ran off the edge of the card with nothing to clamp it.
 *
 * Covering is the right answer rather than a compromise: what is underneath is a name and a level
 * bar for a device that is not connected, so there is nothing being hidden that is worth reading.
 */
function PlayerStatus({ playerNumber, className, ...restProps }: Props) {
  const status = usePlayerMicStatus(playerNumber);

  return (
    <div
      {...restProps}
      className={`relative flex w-full flex-1 items-center justify-end gap-2 ${className}`}
      data-test="player-mic-status">
      <Ping playerNumber={playerNumber} />
      {status === 'ok' ? (
        <div
          data-test="status-ok"
          className="mobile:w-4 mobile:h-4 m-[0.15rem] inline-block h-6 w-6 rounded-full border border-black bg-white"
        />
      ) : (
        // The colour carries the severity, so the glyph that used to does not have to: an icon at
        // this size is unreadable from a sofa, and it took a third of a line the sentence needs.
        // `data-test` stays the status name, which is what the specs look a slot up by.
        <div
          data-test={`status-${status}`}
          data-status={status}
          // Sized by its own text and centred on the slot, rather than stretched to fill this row:
          // with the device gone there is nothing else in the row's flow, so it collapses to zero
          // height and an `inset-0` warning would come out invisible - background and all.
          className={`absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-xl px-1.5 py-1.5 text-center text-xs leading-tight ${
            status === 'unavailable' ? 'bg-red-950/95 text-red-100' : 'bg-amber-950/95 text-amber-100'
          }`}>
          {status === 'unavailable' ? (
            <span>
              The device is <strong>disconnected</strong>. Reconnect it, please.
            </span>
          ) : status === 'silent' ? (
            <span>
              <strong>No sound</strong> from this phone. Check its microphone is on.
            </span>
          ) : (
            <span>
              The connection is <strong>unstable</strong>. Check your Wi-Fi signal.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
export default PlayerStatus;
